/**
 * App.tsx — Admin panel root component and route table.
 *
 * Owns the full client-side routing for the MSTMS admin panel. Wraps the whole
 * app in `<ErrorBoundary>` + `<AuthProvider>` (see `App` at the bottom), then
 * delegates to `AppRoutes` for the route definitions.
 *
 * Routing shape:
 *  - `/login` is public (rendered via the lazy `LoginPage`).
 *  - Everything else (`/*`) is gated by `<RequireAuth>` (token presence) and
 *    wrapped in `<AdminLayout>` (sidebar + app bar chrome). Each individual
 *    page is additionally gated by `<RequirePermission>` for a named permission.
 *
 * Every page component is loaded lazily (`React.lazy` + a shared `<Suspense>`
 * fallback spinner) for code-splitting, so the initial bundle stays small.
 * Mapping from URL path -> permission -> page lives in `AppRoutes` below and is
 * the canonical source of truth for which routes exist in the admin panel.
 */
import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { AdminLayout } from './components/AdminLayout';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Typography, Box, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';

const InvitationsPage = lazy(() => import('./pages/InvitationsPage'));
const LoginPage = lazy(() => import('./pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const DashboardPage = lazy(() => import('./pages/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const UsersPage = lazy(() => import('./pages/UsersPage').then((m) => ({ default: m.UsersPage })));
const TournamentsPage = lazy(() => import('./pages/TournamentsPage').then((m) => ({ default: m.TournamentsPage })));
const TournamentDetailPage = lazy(() => import('./pages/TournamentDetailPage').then((m) => ({ default: m.TournamentDetailPage })));
const TeamsPage = lazy(() => import('./pages/TeamsPage').then((m) => ({ default: m.TeamsPage })));
const TeamDetailPage = lazy(() => import('./pages/TeamDetailPage').then((m) => ({ default: m.TeamDetailPage })));
const MatchesPage = lazy(() => import('./pages/MatchesPage').then((m) => ({ default: m.MatchesPage })));
const MatchDetailPage = lazy(() => import('./pages/MatchDetailPage').then((m) => ({ default: m.MatchDetailPage })));
const StandingsPage = lazy(() => import('./pages/StandingsPage').then((m) => ({ default: m.StandingsPage })));
const ActivityLogPage = lazy(() => import('./pages/ActivityLogPage').then((m) => ({ default: m.ActivityLogPage })));
const SportsPage = lazy(() => import('./pages/SportsPage').then((m) => ({ default: m.SportsPage })));
const BanksPage = lazy(() => import('./pages/BanksPage'));
const SeasonControlPage = lazy(() => import('./pages/SeasonControlPage'));
const SeasonsPage = lazy(() => import('./pages/SeasonsPage').then((m) => ({ default: m.SeasonsPage })));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage').then((m) => ({ default: m.AnalyticsPage })));
const StatisticsPage = lazy(() => import('./pages/StatisticsPage').then((m) => ({ default: m.StatisticsPage })));
const ParentalConsentPage = lazy(() => import('./pages/ParentalConsentPage').then((m) => ({ default: m.ParentalConsentPage })));
const FinancialManagementPage = lazy(() => import('./pages/FinancialManagementPage').then((m) => ({ default: m.FinancialManagementPage })));
const NotificationCenterPage = lazy(() => import('./pages/NotificationCenterPage').then((m) => ({ default: m.NotificationCenterPage })));
const SystemSettingsPage = lazy(() => import('./pages/SystemSettingsPage').then((m) => ({ default: m.SystemSettingsPage })));
const WebsiteContentPage = lazy(() => import('./pages/WebsiteContentPage').then((m) => ({ default: m.WebsiteContentPage })));
const SchoolManagementPage = lazy(() => import('./pages/SchoolManagementPage').then((m) => ({ default: m.SchoolManagementPage })));
const CustomerSupportPage = lazy(() => import('./pages/CustomerSupportPage').then((m) => ({ default: m.CustomerSupportPage })));
const ApiDocsPage = lazy(() => import('./pages/ApiDocsPage').then((m) => ({ default: m.ApiDocsPage })));
const RolesPage = lazy(() => import('./pages/RolesPage').then((m) => ({ default: m.RolesPage })));
const TournamentHierarchyPage = lazy(() => import('./pages/TournamentHierarchyPage').then((m) => ({ default: m.TournamentHierarchyPage })));
const OverallSeasonViewPage = lazy(() => import('./pages/OverallSeasonViewPage').then((m) => ({ default: m.OverallSeasonViewPage })));
const ChampionshipsPage = lazy(() => import('./pages/ChampionshipsPage'));
const RegionsPage = lazy(() => import('./pages/RegionsPage').then((m) => ({ default: m.RegionsPage })));
const PlaygroundsPage = lazy(() => import('./pages/PlaygroundsPage').then((m) => ({ default: m.PlaygroundsPage })));
const PlaygroundDetailPage = lazy(() => import('./pages/PlaygroundDetailPage').then((m) => ({ default: m.PlaygroundDetailPage })));
const TasksPage = lazy(() => import('./pages/TasksPage').then((m) => ({ default: m.TasksPage })));
const SchemaExplorerPage = lazy(() => import('./pages/SchemaExplorerPage').then((m) => ({ default: m.SchemaExplorerPage })));
const AttendanceScannerPage = lazy(() => import('./pages/AttendanceScannerPage'));
const BadgePrintPage = lazy(() => import('./pages/BadgePrintPage'));
const SystemHealthPage = lazy(() => import('./pages/SystemHealthPage').then((m) => ({ default: m.SystemHealthPage })));
const ContractTemplatesPage = lazy(() => import('./pages/ContractTemplatesPage').then((m) => ({ default: m.ContractTemplatesPage })));
const SchedulerPage = lazy(() => import('./pages/SchedulerPage').then((m) => ({ default: m.SchedulerPage })));
const PlayersPage = lazy(() => import('./pages/PlayersPage').then((m) => ({ default: m.PlayersPage })));
const ParticipantsPage = lazy(() => import('./pages/ParticipantsPage').then((m) => ({ default: m.ParticipantsPage })));
const SeasonSetupPage = lazy(() => import('./pages/SeasonSetupPage').then((m) => ({ default: m.SeasonSetupPage })));
const UserDetailPage = lazy(() => import('./pages/UserDetailPage').then((m) => ({ default: m.UserDetailPage })));
const SchoolDetailPage = lazy(() => import('./pages/SchoolDetailPage').then((m) => ({ default: m.SchoolDetailPage })));
const SportDetailPage = lazy(() => import('./pages/SportDetailPage').then((m) => ({ default: m.SportDetailPage })));

/**
 * Route guard for authenticated areas. Renders `children` only when an access
 * token is present in localStorage (`mstms_access_token`); otherwise redirects
 * to `/login`, preserving the attempted location in `state.from` so the login
 * flow can send the user back afterwards.
 *
 * NOTE: this is a presence-only check (no token validation/expiry) — actual
 * permission enforcement is done per-route by `RequirePermission`.
 *
 * @param children The protected element tree to render when authenticated.
 */
function RequireAuth({ children }: { children: JSX.Element }) {
  const location = useLocation();
  const token = typeof window !== 'undefined' ? localStorage.getItem('mstms_access_token') : null;

  if (!token) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children;
}

/**
 * Route guard that checks for a specific permission.
 *
 * Reads `hasPermission` / `loading` from `useAuth()` (AuthContext). While the
 * auth/permission set is still loading it renders nothing (`null`) to avoid a
 * flash of "access denied". Once loaded, it renders `children` if the current
 * user holds `permission`, otherwise an i18n "access denied" message.
 *
 * @param permission The permission key required to view the route, e.g.
 *   `"user_view"`, `"tournament_view"`, `"settings_view"`.
 * @param children The page element to render when permitted.
 */
function RequirePermission({ permission, children }: { permission: string; children: JSX.Element }) {
  const { hasPermission, loading } = useAuth();
  const { t } = useTranslation();

  // Auth/permissions not resolved yet — render nothing to avoid a false denial flash.
  if (loading) return null;

  if (!hasPermission(permission)) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          {t('roles.accessDenied')}
        </Typography>
        <Typography color="text.secondary">
          {t('roles.noPermission')}
        </Typography>
      </Box>
    );
  }

  return children;
}

/**
 * The admin panel's route table.
 *
 * Defines the public `/login` route plus the authenticated `/*` subtree. The
 * authenticated subtree is wrapped in `RequireAuth` -> `AdminLayout` -> nested
 * `<Routes>`, where each page is guarded by `RequirePermission`. A shared
 * `<Suspense>` boundary shows a centered `<CircularProgress>` while a lazily
 * imported page chunk loads.
 *
 * Includes a legacy redirect: `/overall-season-view` -> `/championships`.
 */
function AppRoutes() {
  const { refreshAuth } = useAuth();

  // Called by LoginPage after a successful sign-in; re-fetches the current user
  // and permission set so guards (RequirePermission) see the new session.
  const handleLoginSuccess = async () => {
    await refreshAuth();
  };

  return (
    <Suspense
      fallback={
        <Box sx={{ minHeight: '100vh', display: 'grid', placeItems: 'center' }}>
          <CircularProgress />
        </Box>
      }
    >
      <Routes>
      <Route path="/login" element={<LoginPage onLoginSuccess={handleLoginSuccess} />} />
      <Route
        path="/*"
        element={
          <RequireAuth>
            <AdminLayout>
              <Routes>
                <Route path="/" element={<RequirePermission permission="dashboard_view"><DashboardPage /></RequirePermission>} />
                <Route path="/tasks" element={<RequirePermission permission="task_view"><TasksPage /></RequirePermission>} />
                <Route path="/schema" element={<RequirePermission permission="settings_view"><SchemaExplorerPage /></RequirePermission>} />
                <Route path="/users" element={<RequirePermission permission="user_view"><UsersPage /></RequirePermission>} />
                <Route path="/users/:userId" element={<RequirePermission permission="user_view"><UserDetailPage /></RequirePermission>} />
                <Route path="/invitations" element={<RequirePermission permission="invitation_view"><InvitationsPage /></RequirePermission>} />
                <Route path="/roles" element={<RequirePermission permission="admin_role_view"><RolesPage /></RequirePermission>} />
                <Route path="/tournaments/:tournamentId" element={<RequirePermission permission="tournament_view"><TournamentDetailPage /></RequirePermission>} />
                <Route path="/tournaments" element={<RequirePermission permission="tournament_view"><TournamentsPage /></RequirePermission>} />
                <Route path="/participants" element={<RequirePermission permission="tournament_view"><ParticipantsPage /></RequirePermission>} />
                {/* Groups merged into Participants ("Teams & Groups"); keep the old path working. */}
                <Route path="/groups" element={<Navigate to="/participants" replace />} />
                <Route path="/tournament-hierarchy" element={<RequirePermission permission="tournament_view"><TournamentHierarchyPage /></RequirePermission>} />
                <Route path="/championships" element={<RequirePermission permission="season_view"><ChampionshipsPage /></RequirePermission>} />
                <Route path="/overall-season-view" element={<RequirePermission permission="season_view"><OverallSeasonViewPage /></RequirePermission>} />
                <Route path="/players" element={<RequirePermission permission="team_view"><PlayersPage /></RequirePermission>} />
                <Route path="/teams" element={<RequirePermission permission="team_view"><TeamsPage /></RequirePermission>} />
                <Route path="/teams/:teamId" element={<RequirePermission permission="team_view"><TeamDetailPage /></RequirePermission>} />
                <Route path="/matches" element={<RequirePermission permission="match_view"><MatchesPage /></RequirePermission>} />
                <Route path="/matches/:matchId" element={<RequirePermission permission="match_view"><MatchDetailPage /></RequirePermission>} />
                <Route path="/attendance/scan/:matchId" element={<RequirePermission permission="attendance.scan"><AttendanceScannerPage /></RequirePermission>} />
                <Route path="/attendance/badges/:matchId" element={<RequirePermission permission="attendance.view"><BadgePrintPage /></RequirePermission>} />
                <Route path="/standings" element={<RequirePermission permission="standing_view"><StandingsPage /></RequirePermission>} />
                <Route path="/activity-logs" element={<RequirePermission permission="activity_log_view"><ActivityLogPage /></RequirePermission>} />
                <Route path="/system-health" element={<RequirePermission permission="settings_view"><SystemHealthPage /></RequirePermission>} />
                <Route path="/sports/:sportId" element={<RequirePermission permission="sport_view"><SportDetailPage /></RequirePermission>} />
                <Route path="/sports" element={<RequirePermission permission="sport_view"><SportsPage /></RequirePermission>} />
                <Route path="/banks" element={<RequirePermission permission="settings_view"><BanksPage /></RequirePermission>} />
                <Route path="/season-control" element={<RequirePermission permission="season_view"><SeasonControlPage /></RequirePermission>} />
                <Route path="/seasons" element={<RequirePermission permission="season_view"><SeasonsPage /></RequirePermission>} />
                <Route path="/analytics" element={<RequirePermission permission="analytics_view"><AnalyticsPage /></RequirePermission>} />
                <Route path="/statistics" element={<RequirePermission permission="statistics_view"><StatisticsPage /></RequirePermission>} />
                <Route path="/parental-consent" element={<RequirePermission permission="consent_view"><ParentalConsentPage /></RequirePermission>} />
                <Route path="/financial" element={<RequirePermission permission="financial_view"><FinancialManagementPage /></RequirePermission>} />
                <Route path="/notifications" element={<RequirePermission permission="notification_view"><NotificationCenterPage /></RequirePermission>} />
                <Route path="/regions" element={<RequirePermission permission="region_view"><RegionsPage /></RequirePermission>} />
                <Route path="/website-content" element={<RequirePermission permission="settings_view"><WebsiteContentPage /></RequirePermission>} />
                <Route path="/contract-templates" element={<RequirePermission permission="settings_view"><ContractTemplatesPage /></RequirePermission>} />
                <Route path="/scheduler" element={<RequirePermission permission="settings_view"><SchedulerPage /></RequirePermission>} />
                <Route path="/season-setup" element={<RequirePermission permission="season_view"><SeasonSetupPage /></RequirePermission>} />
                <Route path="/settings" element={<RequirePermission permission="settings_view"><SystemSettingsPage /></RequirePermission>} />
                <Route path="/schools/:schoolId" element={<RequirePermission permission="school_view"><SchoolDetailPage /></RequirePermission>} />
                <Route path="/schools" element={<RequirePermission permission="school_view"><SchoolManagementPage /></RequirePermission>} />
                <Route path="/playgrounds/:playgroundId" element={<RequirePermission permission="playground_view"><PlaygroundDetailPage /></RequirePermission>} />
                <Route path="/playgrounds" element={<RequirePermission permission="playground_view"><PlaygroundsPage /></RequirePermission>} />
                <Route path="/support" element={<RequirePermission permission="support_view"><CustomerSupportPage /></RequirePermission>} />
                <Route path="/api-docs" element={<RequirePermission permission="api_docs_view"><ApiDocsPage /></RequirePermission>} />
              </Routes>
            </AdminLayout>
          </RequireAuth>
        }
      />
      </Routes>
    </Suspense>
  );
}

/**
 * Root application component (default export, mounted by `main.tsx`).
 *
 * Composition order matters:
 *  - `ErrorBoundary` — catches render-time errors anywhere below it.
 *  - `AuthProvider` — supplies auth state / `hasPermission` consumed by the
 *    route guards.
 *  - `AppRoutes` — the route table.
 *
 * Note: providers higher up the tree (router, theme, React Query, i18n, etc.)
 * are set up in `main.tsx`, not here.
 */
export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </ErrorBoundary>
  );
}

